/**
 * IST DIE BUEHNE WIRKLICH GEZEICHNET? — der Beleg hinter dem ok eines Oeffnungsauftrags
 * (ASSIOEFFNEN, 13.10.2026).
 *
 * Bis hierher quittierte der Guide einen Auftrag mit ok, sobald `buehneOeffnen()` true
 * lieferte — also aus dem eigenen Zustand. Ob links die Bühne stand und rechts ihre
 * Fenster statt „Der Guide hat das Wort", hat niemand nachgesehen. Rafael, 13.10.: *„er
 * zeigt mir hier weder links noch rechts eine bühne"*.
 *
 * Geprüft wird deshalb das, was er sieht, am DOM:
 *  - links die offene Bühne MIT dieser Kennung (`guide-buehne-offen`, bei abgeschlossenen
 *    `guide-buehne-historisch`), sichtbar;
 *  - rechts eine sichtbare, nicht überdeckte Bühnenschicht (`data-ai-id="buehne"`,
 *    LayoutManager) und nicht die leere Erklärfläche (`data-ai-id="guide-buehne"`) —
 *    ODER der Vermerk `buehne-auf-b2`: die Bühne steht auf dem gekoppelten Bildschirm 2,
 *    und der hat sie an SEINEM DOM mit `rechtsNichtGezeichnet` belegt, bevor er quittierte.
 * Rechts zählt die Kennung der GEZEIGTEN Bühne (`data-buehne-zeigt`, LayoutManager), nicht
 * `data-buehne-id`: eine angeheftete Bühne behält ihren Schlüssel, wenn ihre Nachfolgerin
 * gezeigt wird (buehnenAnheften.ts, `kette`). Ohne diesen Vergleich belegte die Schicht der
 * VORIGEN Bühne die neue (Prüfurteil ASSIOEFFNENR R2, P2-a); ebenso gilt der Vermerk von
 * Bildschirm 2 nur für die Bühne, die er quittiert hat.
 *
 * SICHTBAR HEISST NICHT „HAT FLÄCHE" (Prüfurteil ASSIOEFFNENR P2-1). Ein inaktiver
 * Arbeitsbereich liegt mit `visibility:hidden`/`opacity:0` voll ausgelegt im Baum
 * (App.tsx, data-workspace-root), Mission Control oder „Alle Chats" decken die Bühne ab.
 * Deshalb: kein verbergender Vorfahr, und rechts trifft `elementFromPoint` an mindestens
 * einem von fünf Punkten die Schicht selbst. Fünf statt einem, weil eine schmale Spalte
 * über der Mitte die Bühne nicht unsichtbar macht; eine Abdeckung über alles dagegen schon.
 */
export type Sichtbar = (el: Element) => boolean;

/** Fläche, und kein Vorfahr blendet aus (display/visibility/opacity). */
export const sichtbarImLayout: Sichtbar = el => {
  const r = el.getBoundingClientRect();
  if (!(r.width > 0 && r.height > 0)) return false;
  const win = el.ownerDocument.defaultView;
  if (!win) return false;
  for (let e: Element | null = el; e; e = e.parentElement) {
    const cs = win.getComputedStyle(e);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.visibility === 'collapse' || cs.opacity === '0') return false;
  }
  return true;
};

/** Zusätzlich: liegt nichts darüber? Ohne `elementFromPoint` nicht prüfbar — dann nicht belegt. */
export const sichtbarUnverdeckt: Sichtbar = el => {
  if (!sichtbarImLayout(el)) return false;
  const doc = el.ownerDocument;
  const win = doc.defaultView;
  if (!win || typeof doc.elementFromPoint !== 'function') return false;
  const r = el.getBoundingClientRect();
  const l = Math.max(r.left, 0), t = Math.max(r.top, 0);
  const re = Math.min(r.right, win.innerWidth), u = Math.min(r.bottom, win.innerHeight);
  if (re <= l || u <= t) return false;
  const punkte: Array<[number, number]> = [[0.5, 0.5], [0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]];
  return punkte.some(([fx, fy]) => {
    const hit = doc.elementFromPoint(l + (re - l) * fx, t + (u - t) * fy);
    return !!hit && (hit === el || el.contains(hit));
  });
};

export interface Pruefer { links: Sichtbar; rechts: Sichtbar }
const STANDARD: Pruefer = { links: sichtbarImLayout, rechts: sichtbarUnverdeckt };

/**
 * Nur die rechte Seite — so prüft Bildschirm 2 seinen eigenen DOM, bevor er quittiert.
 * `buehneId`: die Schicht muss genau diese Bühne zeigen (`data-buehne-zeigt`).
 */
export function rechtsNichtGezeichnet(root: ParentNode, buehneId: string, sichtbar: Sichtbar = STANDARD.rechts): string | null {
  const platzhalter = root.querySelector('[data-ai-id="guide-buehne"]');
  if (platzhalter && sichtbarImLayout(platzhalter)) return 'Rechts steht die leere Fläche „Der Guide hat das Wort", nicht die Bühne.';
  const alle = [...root.querySelectorAll<HTMLElement>('[data-ai-id="buehne"]')];
  if (!alle.length) return 'Rechts ist keine Bühnenschicht vorhanden.';
  const diese = alle.filter(el => el.dataset.buehneZeigt === buehneId);
  if (!diese.length) {
    const andere = alle.map(el => el.dataset.buehneZeigt ?? '?');
    return `Rechts steht eine andere Bühne (${andere.join(', ')}), nicht ${buehneId}.`;
  }
  if (!diese.some(sichtbar)) return 'Die Bühnenschicht ist verborgen oder überdeckt (anderer Arbeitsbereich aktiv, Mission Control oder Alle Chats offen).';
  return null;
}

/** Steht DIESE Bühne auf dem gekoppelten Bildschirm 2 — dort gezeichnet und quittiert? */
export function rechtsAufBildschirm2(root: ParentNode, buehneId: string): boolean {
  return [...root.querySelectorAll<HTMLElement>('[data-ai-id="buehne-auf-b2"]')].some(el => el.dataset.buehneId === buehneId);
}

/**
 * Je Frame nachsehen, bis `pruefe` null liefert oder `maxMs` um sind. Erst einen Frame
 * abwarten, damit nicht die Schicht der vorigen Bühne mitzählt. Liefert den letzten Satz.
 */
export async function warteBisBelegt(pruefe: () => string | null, maxMs: number,
  frame: () => Promise<void> = () => new Promise(r => requestAnimationFrame(() => r()))): Promise<string | null> {
  const bis = performance.now() + maxMs;
  await frame();
  let fehlt = pruefe();
  while (fehlt && performance.now() < bis) { await frame(); fehlt = pruefe(); }
  return fehlt;
}

/** null = gezeichnet; sonst der Satz, was fehlt. */
export function buehneNichtGezeichnet(root: ParentNode, buehneId: string, pruefer: Pruefer = STANDARD): string | null {
  const links = [...root.querySelectorAll<HTMLElement>('[data-ai-id="guide-buehne-offen"], [data-ai-id="guide-buehne-historisch"]')];
  const linksDiese = links.find(el => el.dataset.buehneId === buehneId);
  if (!linksDiese) {
    const andere = links.map(el => el.dataset.buehneId).filter(Boolean);
    return andere.length
      ? `Links steht eine andere Bühne (${andere.join(', ')}), nicht ${buehneId}.`
      : `Links steht keine offene Bühne.`;
  }
  if (!pruefer.links(linksDiese)) return 'Die Bühne steht links im Guide, ist aber nicht sichtbar (Spalte eingeklappt oder verdeckt).';
  // Vor dem Platzhalter: bei Kopplung zeigt Bildschirm 1 rechts absichtlich die leere Fläche.
  if (rechtsAufBildschirm2(root, buehneId)) return null;
  return rechtsNichtGezeichnet(root, buehneId, pruefer.rechts);
}
